import React, { useState, useEffect } from "react";
import { useLiquiditySweepData } from "./useLiquiditySweepData";
import styles from "../Chart.module.css";
import { Row, Container, Col } from "reactstrap";
import LiquidityZoneInfo from "../Info/LiquiditySweepInfo"; // Import the LiquidityZoneInfo component
import { FaInfoCircle } from "react-icons/fa";
import { useAppContext } from "../../../App"; // Import AppContext

const LiquiditySweepChart = () => {

    const {
        currencyPair, setCurrencyPair, timeInterval, setTimeInterval, timeZone, setTimeZone, timeZones, g50Currencies, timeIntervals, Plot, LoadingSpinner, Dropdown, dateRange, setDateRange, handleRelayout, availableDates, setAvailableDates, currentPrice, setCurrentPrice
    } = useAppContext(); // Destructure values from AppContext

    const [gapThreshold, setGapThreshold] = useState(0.0001);
    const [maxDifference, setMaxDifference] = useState(0.2); // Default max

    const [isInfoOpen, setIsInfoOpen] = useState(false); // Manage LiquidityZoneInfo state
    const toggleInfoDrawer = () => {
        setIsInfoOpen((prev) => !prev); // Toggle LiquidityZoneInfo drawer
    };

    const { chartData, loading, error } = useLiquiditySweepData(currencyPair, timeInterval, timeZone, gapThreshold, setMaxDifference, dateRange, setDateRange, availableDates, setAvailableDates, currentPrice, setCurrentPrice);

    const handleCurrencyPairChange = (value) => {
        //setCurrencyPair (value);
        setCurrencyPair(value); // Update global state
        setGapThreshold(0.0001)
    };

    const handleTimeIntervalChange = (value) => {
        //setTimeInterval(value);
        setTimeInterval(value); // Update global state
    };

    const handleTimeZoneChange = (value) => {
        //setLocalTimeZone(value);
        setTimeZone(value); // Update global state
    };

    const handleGapThresholdChange = (e) => {
        const rawValue = parseFloat(e.target.value);
        const roundedValue = Math.round(rawValue / 0.0001) * 0.0001; // Replace 0.0001 with your step value
        setGapThreshold(parseFloat(roundedValue.toFixed(4))); // Limit to 4 decimal places
    };

    if (loading) {
        return <LoadingSpinner message="Loading chart data..." />;
    }

    if (error) {
        return <p style={{ color: "red" }}>{error}</p>;
    }

    return (
        <Container>
            <Row>
                <div>
                    {/* Header with Info Icon */}
                    {/* LiquidityZoneInfo Drawer */}
                    <LiquidityZoneInfo isOpen={isInfoOpen} toggleDrawer={toggleInfoDrawer} />
                    <div className={styles.header}>
                        <h2 className={styles.chartTitle}>
                            Liquidity Sweep Analysis - {currencyPair} {currentPrice}
                            <FaInfoCircle
                                onClick={toggleInfoDrawer}
                                style={{
                                    marginLeft: "20px",
                                    cursor: "pointer",
                                    fontSize: "20px",
                                    verticalAlign: "middle",
                                }}
                                title="Open Liquidity Zone Info" // Tooltip for accessibility
                            />
                        </h2>
                    </div>
                </div>
                {/* Dropdowns */}
                <Col xs={4} md={4}>
                    <Dropdown
                        label={`Currency`}
                        options={g50Currencies.map((currency) => ({
                            label: `${currency.country} (${currency.pair})`,
                            value: currency.pair,
                        }))}
                        value={currencyPair}
                        onChange={handleCurrencyPairChange}
                    />
                </Col>
                <Col xs={4} md={4}>
                    <Dropdown
                        label="Time Interval"
                        options={timeIntervals}
                        value={timeInterval}
                        onChange={handleTimeIntervalChange}
                    />
                </Col>
                <Col xs={4} md={4}>
                    <Dropdown
                        label="Time Zone"
                        options={timeZones.map((zone) => ({
                            label: zone.label,
                            value: zone.value,
                        }))}
                        value={timeZone}
                        onChange={handleTimeZoneChange}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={6} md={6}>
                    <div className={styles.sliderContainer}>
                        <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                            Displacement Threshold
                        </label>
                    </div>
                </Col>
                <Col xs={6} md={6}>
                    {availableDates.start && availableDates.end && (
                        <div className={styles.sliderContainer}>
                            <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                                Available Date Range: {new Date(availableDates.start).toLocaleDateString()} -{" "}
                                {new Date(availableDates.end).toLocaleDateString()}
                            </label>
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={6} md={6}>
                    <div className={styles.sliderContainer}>
                        <input
                            type="range"
                            id="gapThreshold"
                            min="0.0001"
                            max={maxDifference}
                            step={maxDifference / 10}
                            value={gapThreshold}
                            onChange={(e) => handleGapThresholdChange(e)}
                            onBlur={(e) => setGapThreshold((prev) => Math.round(prev / 0.0001) * 0.0001)} // Snap to step on blur
                            className={styles.slider}
                        />

                    </div>
                </Col>
                <Col xs={6} md={6}>
                    {availableDates.start && availableDates.end && (
                        <div className={styles.sliderContainer}>
                            <input
                                type="range"
                                id="dateRangeSlider"
                                min={new Date(availableDates.start).getTime()} // Minimum is the start of available dates
                                max={new Date(availableDates.end).getTime()} // Maximum is the end of available dates
                                step={86400000} // 1 day in milliseconds
                                value={new Date(dateRange.start).getTime()} // Bind slider to the start date
                                onChange={(e) => {
                                    const start = new Date(parseInt(e.target.value)).toISOString(); // Parse slider value to ISO format
                                    setDateRange((prev) => ({ ...prev, start })); // Update the start date
                                }}
                                className={styles.slider}
                            />
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={6} md={6}>
                    <div className={styles.sliderContainer}>
                        <label htmlFor="gapThreshold" className={styles.sliderLabel}>
                            Selected Threshold: {gapThreshold.toFixed(4)} - {maxDifference}
                        </label>
                    </div>
                </Col>
                <Col xs={6} md={6}>
                    {availableDates.start && availableDates.end && (
                        <div className={styles.sliderContainer}>
                            <label htmlFor="dateRangeSlider" className={styles.sliderLabel}>
                                Selected Start Date: {new Date(dateRange.start).toLocaleDateString()}
                            </label>
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                </Col>
            </Row>
            {/* Plotly Chart */}
            <Row>
                <Col md={12}>
                    {chartData && (
                        <Plot
                            data={chartData.traces}
                            layout={{
                                ...chartData.layout,
                                margin: { t: 40, r: 40, b: 40, l: 40 },
                                autosize: true,
                                legend: { orientation: "h", x: 0, y: 40 },
                            }}
                            onRelayout={handleRelayout} // Listen to relayout events
                            config={{ responsive: true, scrollZoom: true }}
                            style={{ width: "100%", height: "65vh" }}
                        />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default LiquiditySweepChart;
